import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/lib/adminLang";

const kompakt = (n) => {
  const v = Number(n) || 0;
  const abs = Math.abs(v);
  if (abs >= 1e6) return `${(v / 1e6).toFixed(2)}m`;
  if (abs >= 1e3) return `${Math.round(v / 1e3)}k`;
  return `${Math.round(v)}`;
};

const timeAgo = (at, lang) => {
  const diff = Date.now() - new Date(at).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return lang === "no" ? "nå" : "now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}${lang === "no" ? "t" : "h"}`;
  return `${Math.floor(h / 24)}d`;
};

const StatKort = ({ label, til, children, testid, nav }) => (
  <button
    type="button"
    onClick={() => til && nav(til)}
    className="adm-card group relative px-5 py-4 text-left transition-colors hover:border-neutral-300"
    data-testid={testid}
  >
    <ArrowUpRight
      size={13}
      className="absolute top-4 right-4 text-neutral-400 opacity-0 -translate-x-1 translate-y-1 transition-all duration-200 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0"
    />
    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-400 mb-2.5">{label}</p>
    {children}
  </button>
);

const Stor = ({ children, tone }) => (
  <p
    className={`text-[30px] font-medium tracking-[-0.02em] tabular-nums leading-none ${tone || "text-neutral-900"}`}
    style={{ fontFamily: '"Switzer", "Instrument Sans", sans-serif' }}
  >
    {children}
  </p>
);
const Sub = ({ children }) => <p className="text-[11px] text-neutral-400 mt-2 leading-relaxed">{children}</p>;

const Bar = ({ pct, tone }) => (
  <div className="adm-bar mt-3">
    <span style={{ width: `${Math.max(0, Math.min(100, pct))}%`, background: tone || "#F5F1E8" }} />
  </div>
);

const Spark = ({ points }) => {
  if (!points || points.length < 2) return null;
  const W = 600, H = 46, P = 3;
  const vals = points.map((p) => p.closing);
  const min = Math.min(0, ...vals);
  const max = Math.max(0, ...vals);
  const x = (i) => P + (i / (vals.length - 1)) * (W - 2 * P);
  const y = (v) => P + (1 - (v - min) / (max - min || 1)) * (H - 2 * P);
  const neg = vals.some((v) => v < 0);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-[46px] mt-4" data-testid="overview-cash-spark">
      {min < 0 && <line x1={P} x2={W - P} y1={y(0)} y2={y(0)} stroke="currentColor" strokeOpacity="0.18" strokeDasharray="3 3" />}
      <polyline
        points={vals.map((v, i) => `${x(i)},${y(v)}`).join(" ")}
        fill="none"
        stroke={neg ? "#D48A7E" : "#8FB08C"}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const RadStat = ({ label, verdi, tone, context, pct, barTone, til, testid, nav }) => (
  <button
    type="button"
    onClick={() => nav(til)}
    className="adm-feed-rad group w-full grid grid-cols-[84px_76px_1fr] sm:grid-cols-[110px_100px_1fr_140px_20px] items-center gap-2.5 sm:gap-3 px-4 sm:px-5 py-3.5 border-t border-[#F1EEE7] first:border-0 text-left transition-colors"
    data-testid={testid}
  >
    <span className="text-[9.5px] font-semibold uppercase tracking-[0.16em] text-neutral-400">{label}</span>
    <span className={`text-[19px] font-medium tabular-nums leading-none ${tone || "text-neutral-900"}`}>{verdi}</span>
    <span className="text-[11px] text-neutral-400 truncate">{context}</span>
    <span className="hidden sm:block">
      <span className="adm-bar block !mt-0">
        <span style={{ width: `${Math.max(0, Math.min(100, pct))}%`, background: barTone || "#F5F1E8" }} />
      </span>
    </span>
    <ArrowUpRight size={12} className="adm-feed-pil hidden sm:block text-neutral-400" />
  </button>
);

const PanelHead = ({ children, count }) => (
  <div className="px-5 py-3 bg-[#F7F5F0] border-b border-[#EFECE4] flex items-center justify-between">
    <span className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-neutral-500">{children}</span>
    {count != null && <span className="text-[11px] tabular-nums text-neutral-400">{count}</span>}
  </div>
);

export default function ControlCenter() {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  const { t, lang } = useLang();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/os/overview");
      setD(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || t("Could not load the overview"));
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  if (!d) {
    return <p className="text-center text-xs text-neutral-400 py-20" data-testid="overview-laster">{t("Loading Control Center …")}</p>;
  }

  const settDato = async () => {
    const dato = window.prompt(t("Event date (YYYY-MM-DD):"), d.event.eventDate || "2027-04-15");
    if (!dato) return;
    try {
      await api.patch("/admin/os/event", { eventDate: dato });
      await load();
      toast.success(t("Event date set"));
    } catch (e) {
      toast.error(t("Invalid date"));
    }
  };

  const datoKort = (iso) =>
    new Date(iso).toLocaleDateString(lang === "no" ? "nb-NO" : "en-GB", { day: "numeric", month: "short" });
  const dagerOver = (iso) => Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 86400000));

  const gaaTil = { speaker: "/admin/speakers", session: "/admin/program", conflict: "/admin/program", task: "/admin/saker", budget: "/admin/budsjett", partner: "/admin/partners", experience: "/admin/experience" };
  const modulTil = { Saker: "/admin/saker", Partners: "/admin/partners", Speakers: "/admin/speakers", Budget: "/admin/budsjett" };

  const spPct = d.speakers.total ? (d.speakers.confirmed / d.speakers.total) * 100 : 0;
  const paTot = (d.partners.signedCount ?? 0) + (d.partners.pipelineCount ?? 0);
  const paPct = paTot ? ((d.partners.signedCount ?? 0) / paTot) * 100 : 0;
  const tiPct = d.tickets.capacity ? (d.tickets.earlyAccess / d.tickets.capacity) * 100 : 0;
  const buPct = d.budget && d.budget.revenue ? (d.budget.profit / d.budget.revenue) * 100 : 0;
  const saPct = d.tasks.open ? (d.tasks.overdue / d.tasks.open) * 100 : 0;
  const exPct = d.experiences?.total ? ((d.experiences.confirmed ?? 0) / d.experiences.total) * 100 : 0;

  return (
    <div data-testid="control-center">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-7">
        <div>
          <p className="adm-eyebrow mb-1.5">Control Center</p>
          <h1 className="adm-title">{d.event.name}</h1>
          <p className="adm-sub mt-1.5">{d.event.venue} · {d.event.eventDate || t("date not set")} · {t("capacity")} {d.event.capacity?.toLocaleString("nb-NO")}</p>
        </div>
        <button type="button" onClick={settDato} className="text-right group" data-testid="overview-days">
          {d.daysToShow != null ? (
            <>
              <p
                className="font-medium tracking-[-0.03em] tabular-nums leading-none text-neutral-900"
                style={{ fontFamily: '"Switzer", "Instrument Sans", sans-serif', fontSize: "clamp(48px, 5vw, 76px)" }}
              >
                {d.daysToShow}
              </p>
              <p className="text-[10px] uppercase tracking-[0.26em] text-neutral-400 mt-1.5">{t("days to show")}</p>
            </>
          ) : (
            <span className="adm-btn-ghost adm-btn-sm">{t("Set event date")}</span>
          )}
        </button>
      </header>

      <div className="adm-rise">
        {/* Bento: finansiell hero + readiness-kolonne */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <button
            type="button"
            onClick={() => nav("/admin/budsjett")}
            className="adm-card group relative lg:col-span-2 px-6 py-6 text-left transition-colors hover:border-neutral-300 flex flex-col justify-between"
            data-testid="overview-budget"
          >
            <ArrowUpRight
              size={13}
              className="absolute top-5 right-5 text-neutral-400 opacity-0 -translate-x-1 translate-y-1 transition-all duration-200 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0"
            />
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-400">{t("Budget · base forecast")}</p>
            {d.budget ? (
              <>
                <div className="mt-3 flex flex-wrap items-baseline gap-x-5 gap-y-1">
                  <p
                    className={`adm-num-hero tabular-nums leading-none ${d.budget.profit < 0 ? "text-red-600" : "text-emerald-700"}`}
                    style={{ fontSize: "clamp(38px, 4.5vw, 56px)" }}
                  >
                    {kompakt(d.budget.profit)}
                  </p>
                  <p className="text-[17px] font-medium tabular-nums text-neutral-500">
                    {d.budget.revenue ? `${buPct.toFixed(1)} % ${t("margin")}` : ""}
                  </p>
                </div>
                <Sub>
                  {t("revenue")} {kompakt(d.budget.revenue)} · {t("costs")} {kompakt(d.budget.costs)} · {d.budget.vsBudget >= 0 ? "+" : ""}{kompakt(d.budget.vsBudget)} {t("vs budget")}
                  {d.budget.peak ? ` · ${t("funding")} ${kompakt(d.budget.peak.amount)}` : ""}
                </Sub>
                <Spark points={d.budget.cashSpark} />
              </>
            ) : (
              <Sub>{t("Budget not set up yet")}</Sub>
            )}
          </button>

          <div className="flex flex-col gap-3">
            <StatKort label="Speakers" til="/admin/speakers" nav={nav} testid="overview-speakers">
              <Stor>{d.speakers.confirmed}</Stor>
              <Sub>{t("confirmed")} · {d.speakers.active} {t("active")} · {d.speakers.awaiting} {t("awaiting")}{d.speakers.blockers > 0 ? ` · ${d.speakers.blockers} ${t("blockers")}` : ""}</Sub>
              <Bar pct={spPct} />
            </StatKort>
            <StatKort label="Program" til="/admin/program" nav={nav} testid="overview-program">
              <Stor tone={d.program.blockers > 0 ? "text-amber-600" : undefined}>{d.program.completeness}%</Stor>
              <Sub>{d.program.total} {t("sessions")} · {d.program.published} {t("published")} · {d.program.blockers} {t("blockers")}</Sub>
              <Bar pct={d.program.completeness} tone={d.program.blockers > 0 ? "#E5B84B" : undefined} />
            </StatKort>
          </div>
        </div>

        {/* Tett sekundærliste */}
        <div className="adm-card mt-3 overflow-hidden" data-testid="overview-secondary">
          <RadStat
            label="Partners" til="/admin/partners" nav={nav} testid="overview-partners"
            verdi={kompakt(d.partners.signed)}
            context={`${t("signed")} (${d.partners.signedCount ?? 0}) · ${kompakt(d.partners.weightedPipeline)} ${t("weighted pipeline")} · ${d.partners.published ?? 0} ${t("on the website")}`}
            pct={paPct}
          />
          <RadStat
            label="Tickets" til="/admin/signups" nav={nav} testid="overview-tickets"
            verdi={d.tickets.earlyAccess.toLocaleString("nb-NO")}
            context={`${t("Early Access signups")} · ${t("capacity")} ${d.tickets.capacity?.toLocaleString("nb-NO")}`}
            pct={tiPct}
          />
          <RadStat
            label="Saker" til="/admin/saker" nav={nav} testid="overview-tasks"
            verdi={d.tasks.overdue}
            tone={d.tasks.overdue > 0 ? "text-red-600" : undefined}
            context={`${t("overdue")} · ${d.tasks.dueSoon} ${t("due next 7 days")} · ${d.tasks.open} ${t("open")}`}
            pct={saPct}
            barTone={d.tasks.overdue > 0 ? "#D48A7E" : undefined}
          />
          <RadStat
            label={t("Experience")} til="/admin/experience" nav={nav} testid="overview-experience"
            verdi={`${d.experiences?.confirmed ?? 0}/${d.experiences?.total ?? 0}`}
            context={`${t("confirmed")}${d.experiences?.blockers > 0 ? ` · ${d.experiences.blockers} ${t("blockers")}` : ""}`}
            pct={exPct}
          />
        </div>

        {/* Needs attention */}
        <div className="adm-card mt-3 overflow-hidden" data-testid="overview-attention">
          <PanelHead count={d.needsAttention.length}>{t("Needs attention")}</PanelHead>
          {d.needsAttention.length === 0 ? (
            <p className="text-[12.5px] text-neutral-400 text-center py-10" data-testid="overview-attention-tom">
              {t("Nothing urgent. Well done.")}
            </p>
          ) : (
            d.needsAttention.map((a, i) => (
              <button
                key={i}
                type="button"
                onClick={() => gaaTil[a.kind] && nav(gaaTil[a.kind])}
                className="adm-feed-rad w-full flex items-center gap-3 px-5 py-2.5 border-t border-[#F1EEE7] first:border-0 text-left transition-colors"
                data-testid={`attention-rad-${i}`}
              >
                <span className={`w-[7px] h-[7px] shrink-0 ${a.tone === "red" ? "bg-red-500" : "bg-amber-400"}`} />
                <span className="text-[13px] text-neutral-800">{a.text}</span>
                <ArrowUpRight size={12} className="adm-feed-pil ml-auto shrink-0 text-neutral-400" />
              </button>
            ))
          )}
        </div>

        {/* Neste 7 dager + siste aktivitet */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
          <div className="adm-card overflow-hidden" data-testid="overview-upcoming">
            <PanelHead count={(d.upcomingTasks || []).length}>{t("Next 7 days")}</PanelHead>
            {(d.upcomingTasks || []).length === 0 ? (
              <p className="text-[12px] text-neutral-400 text-center py-8">{t("No deadlines in the next 7 days.")}</p>
            ) : (
              d.upcomingTasks.map((u, i) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => nav("/admin/saker")}
                  className="adm-feed-rad w-full flex items-center gap-3 px-5 py-2.5 border-t border-[#F1EEE7] first:border-0 text-left transition-colors"
                  data-testid={`upcoming-rad-${i}`}
                >
                  <span className={`w-[7px] h-[7px] shrink-0 ${u.overdue ? "bg-red-500" : "bg-neutral-300"}`} />
                  <span className="text-[12.5px] text-neutral-800 truncate">{u.title}</span>
                  <span className={`ml-auto shrink-0 text-[11px] tabular-nums ${u.overdue ? "text-red-600 font-medium" : "text-neutral-400"}`}>
                    {u.overdue ? `${dagerOver(u.dueDate)} ${t("d overdue")}` : datoKort(u.dueDate)}
                  </span>
                </button>
              ))
            )}
          </div>

          <div className="adm-card overflow-hidden" data-testid="overview-activity">
            <PanelHead>{t("Latest activity")}</PanelHead>
            {(d.recentActivity || []).length === 0 ? (
              <p className="text-[12px] text-neutral-400 text-center py-8">{t("No activity yet.")}</p>
            ) : (
              d.recentActivity.map((a, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => modulTil[a.module] && nav(modulTil[a.module])}
                  className="adm-feed-rad w-full flex items-center gap-2.5 px-5 py-2.5 border-t border-[#F1EEE7] first:border-0 text-left transition-colors"
                  title={`${a.entity} — ${a.text}`}
                  data-testid={`activity-rad-${i}`}
                >
                  <span className="shrink-0 text-[9px] font-semibold uppercase tracking-[0.1em] text-neutral-400 border border-[#E7E4DB] rounded-full px-2 py-0.5">
                    {a.module === "Budget" ? t("Budget") : a.module}
                  </span>
                  <span className="text-[12px] text-neutral-800 truncate">
                    <b className="font-medium">{a.entity}</b>{a.entity ? " — " : ""}{a.text}
                  </span>
                  <span className="ml-auto shrink-0 text-[10.5px] tabular-nums text-neutral-400">{timeAgo(a.at, lang)}</span>
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
